import type { RecoveryPublicKeyJwk } from '../crypto/recovery.ts';

export const RECOVERY_KEY_DB = 'farfar-recovery-v1';
export const RECOVERY_KEYS_STORE = 'keys';
export const RECOVERY_REPLAY_STORE = 'replays';

export type RecoveryStoredKeyKind = 'membership' | 'device' | 'target';

export interface RecoveryStoredKey {
  kind: RecoveryStoredKeyKind;
  publicKeyFingerprint: string;
  publicKeyJwk: RecoveryPublicKeyJwk;
  privateKey: CryptoKey;
  membershipId?: string;
  name?: string;
  createdAt: string;
}

export interface RecoveryKeyStore {
  putKey(record: RecoveryStoredKey): Promise<void>;
  getKeyByFingerprint(fingerprint: string): Promise<RecoveryStoredKey | null>;
  getKeyByMembershipId(membershipId: string): Promise<RecoveryStoredKey | null>;
  deleteKeyByFingerprint(fingerprint: string): Promise<void>;
  listKeys(): Promise<RecoveryStoredKey[]>;
  hasReplay(replayKey: string): Promise<boolean>;
  commitReplays(replayKeys: string[]): Promise<void>;
}

export class RecoveryKeyStoreError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'RecoveryKeyStoreError';
  }
}

function unsupported(message: string): never {
  throw new RecoveryKeyStoreError(message);
}

export function isNonExtractablePrivateCryptoKey(value: unknown): value is CryptoKey {
  if (!value || typeof value !== 'object') return false;
  const key = value as CryptoKey;
  if (key.type !== 'private' || key.extractable !== false) return false;
  if (typeof CryptoKey !== 'undefined' && !(value instanceof CryptoKey)) return false;
  return true;
}

function assertPersistablePrivateKey(privateKey: CryptoKey): void {
  if (!isNonExtractablePrivateCryptoKey(privateKey)) {
    unsupported('Recovery private keys must be non-extractable CryptoKey objects.');
  }
}

export function createMemoryRecoveryKeyStore(): RecoveryKeyStore {
  const keys = new Map<string, RecoveryStoredKey>();
  const replays = new Set<string>();
  return {
    async putKey(record) {
      assertPersistablePrivateKey(record.privateKey);
      keys.set(record.publicKeyFingerprint, { ...record });
    },
    async getKeyByFingerprint(fingerprint) {
      return keys.get(fingerprint) || null;
    },
    async getKeyByMembershipId(membershipId) {
      return (
        [...keys.values()].find((record) => record.membershipId === membershipId) ||
        null
      );
    },
    async deleteKeyByFingerprint(fingerprint) {
      keys.delete(fingerprint);
    },
    async listKeys() {
      return [...keys.values()];
    },
    async hasReplay(replayKey) {
      return replays.has(replayKey);
    },
    async commitReplays(replayKeys) {
      for (const replayKey of replayKeys) replays.add(replayKey);
    },
  };
}

function openRecoveryDatabase(): Promise<IDBDatabase> {
  if (typeof indexedDB === 'undefined') {
    unsupported(
      'This browser cannot store recovery keys. IndexedDB is required and localStorage is not used.'
    );
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(RECOVERY_KEY_DB, 1);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(RECOVERY_KEYS_STORE)) {
        const store = db.createObjectStore(RECOVERY_KEYS_STORE, {
          keyPath: 'publicKeyFingerprint',
        });
        store.createIndex('membershipId', 'membershipId', { unique: false });
        store.createIndex('kind', 'kind', { unique: false });
      }
      if (!db.objectStoreNames.contains(RECOVERY_REPLAY_STORE)) {
        db.createObjectStore(RECOVERY_REPLAY_STORE, { keyPath: 'replayKey' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(
        new RecoveryKeyStoreError(
          'Could not open the recovery key database in this browser profile.'
        )
      );
  });
}

function requestPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () =>
      reject(new RecoveryKeyStoreError('Recovery key storage failed.'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () =>
      reject(new RecoveryKeyStoreError('Recovery key storage transaction failed.'));
    transaction.onabort = () =>
      reject(new RecoveryKeyStoreError('Recovery key storage transaction aborted.'));
  });
}

export function createIndexedDbRecoveryKeyStore(): RecoveryKeyStore {
  return {
    async putKey(record) {
      assertPersistablePrivateKey(record.privateKey);
      const db = await openRecoveryDatabase();
      try {
        const transaction = db.transaction(RECOVERY_KEYS_STORE, 'readwrite');
        transaction.objectStore(RECOVERY_KEYS_STORE).put(record);
        await transactionDone(transaction);
      } finally {
        db.close();
      }
    },

    async getKeyByFingerprint(fingerprint) {
      const db = await openRecoveryDatabase();
      try {
        const record = await requestPromise(
          db.transaction(RECOVERY_KEYS_STORE, 'readonly')
            .objectStore(RECOVERY_KEYS_STORE)
            .get(fingerprint)
        );
        return record || null;
      } finally {
        db.close();
      }
    },

    async getKeyByMembershipId(membershipId) {
      const db = await openRecoveryDatabase();
      try {
        const record = await requestPromise(
          db.transaction(RECOVERY_KEYS_STORE, 'readonly')
            .objectStore(RECOVERY_KEYS_STORE)
            .index('membershipId')
            .get(membershipId)
        );
        return record || null;
      } finally {
        db.close();
      }
    },

    async deleteKeyByFingerprint(fingerprint) {
      const db = await openRecoveryDatabase();
      try {
        const transaction = db.transaction(RECOVERY_KEYS_STORE, 'readwrite');
        transaction.objectStore(RECOVERY_KEYS_STORE).delete(fingerprint);
        await transactionDone(transaction);
      } finally {
        db.close();
      }
    },

    async listKeys() {
      const db = await openRecoveryDatabase();
      try {
        const records = await requestPromise(
          db.transaction(RECOVERY_KEYS_STORE, 'readonly')
            .objectStore(RECOVERY_KEYS_STORE)
            .getAll()
        );
        return records || [];
      } finally {
        db.close();
      }
    },

    async hasReplay(replayKey) {
      const db = await openRecoveryDatabase();
      try {
        const record = await requestPromise(
          db.transaction(RECOVERY_REPLAY_STORE, 'readonly')
            .objectStore(RECOVERY_REPLAY_STORE)
            .get(replayKey)
        );
        return Boolean(record);
      } finally {
        db.close();
      }
    },

    async commitReplays(replayKeys) {
      const unique = [...new Set(replayKeys.filter(Boolean))];
      if (!unique.length) return;
      const db = await openRecoveryDatabase();
      try {
        const transaction = db.transaction(RECOVERY_REPLAY_STORE, 'readwrite');
        const store = transaction.objectStore(RECOVERY_REPLAY_STORE);
        const committedAt = new Date().toISOString();
        for (const replayKey of unique) {
          store.put({ replayKey, committedAt });
        }
        await transactionDone(transaction);
      } finally {
        db.close();
      }
    },
  };
}

export async function probeRecoveryKeyPersistence(
  store: RecoveryKeyStore = createIndexedDbRecoveryKeyStore()
): Promise<void> {
  if (typeof crypto === 'undefined' || !crypto.subtle) {
    unsupported(
      'This browser cannot run recovery crypto. WebCrypto is required.'
    );
  }
  const pair = (await crypto.subtle.generateKey(
    { name: 'ECDH', namedCurve: 'P-256' },
    false,
    ['deriveBits']
  )) as CryptoKeyPair;
  if (pair.privateKey.extractable) {
    unsupported('This browser generated an extractable recovery private key.');
  }
  const probeId = `probe:${Date.now()}`;
  const publicKeyJwk = (await crypto.subtle.exportKey(
    'jwk',
    pair.publicKey
  )) as RecoveryPublicKeyJwk;
  await store.putKey({
    kind: 'device',
    publicKeyFingerprint: probeId,
    publicKeyJwk: {
      kty: 'EC',
      crv: 'P-256',
      x: String(publicKeyJwk.x),
      y: String(publicKeyJwk.y),
    },
    privateKey: pair.privateKey,
    name: 'persistence-probe',
    createdAt: new Date().toISOString(),
  });
  const stored = await store.getKeyByFingerprint(probeId);
  await store.deleteKeyByFingerprint(probeId);
  if (!isNonExtractablePrivateCryptoKey(stored?.privateKey)) {
    unsupported(
      'This browser cannot persist non-extractable recovery keys in IndexedDB. Another profile or browser is required.'
    );
  }
}
